import React, { useState, useMemo, useEffect } from "react"
import { Form, Button, Row, Col, Spinner } from "react-bootstrap"
import { useParams, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import useAuthAxios from "../hooks/useAuthAxios"
import Layout from "../components/layout/Layout"
import WorkingDaysForm from "../components/users/WorkingDaysForm"
import OutOfOfficeForm from "../components/users/OutOfOfficeForm"
import { toast } from "react-toastify"
import BackButton from "../components/common/BackButton"
import { login } from "../services/auth"
import { validatePassword } from "../utils/validationUtils"

const UserDetails = () => {
  const { userId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, appLookups, isAdmin } = useAuth()
  const { get, put, post } = useAuthAxios()
  const isProfilePage = location.pathname === "/profile"
  const isNewUser = userId === "new"

  const userRoles = useMemo(() => {
    return appLookups?.filter(lookup => lookup.Table === "UsersRoles") || []
  }, [appLookups])

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userData, setUserData] = useState(() => {
    if (isProfilePage) {
      return {
        UserID: user.id,
        RoleID: user.RoleID,
        Firstname: user.Firstname,
        LastName: user.LastName,
        Mail: user.Mail,
        Phone: user.Phone,
        Mobile: user.Mobile
      }
    }
    if (isNewUser) {
      return {
        Active: true,
        RoleID: "",
        Firstname: "",
        LastName: "",
        LoginUsername: "",
        Mail: "",
        Phone: "",
        Mobile: ""
      }
    }
    return null
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    password: "",
    confirmPassword: ""
  })
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, errors: [] })

  useEffect(() => {
    const fetchUserData = async () => {
      if (!isProfilePage && !isNewUser) {
        setIsLoading(true)
        try {
          const response = await get("/users", {
            params: { UserID: userId }
          })
          if (response.success && response.data?.Results?.APIuser?.length > 0) {
            setUserData(response.data.Results.APIuser[0])
          } else {
            toast.error(response.data?.Errors?.Problem || "Ο χρήστης δεν βρέθηκε")
          }
        } catch (err) {
          toast.error("Σφάλμα φόρτωσης δεδομένων χρήστη:", err)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchUserData()
  }, [userId, isProfilePage, isNewUser, get])

  const handleInputChange = (nameOrEvent, value) => {
    if (nameOrEvent.target) {
      const { name, value: eventValue, type, checked } = nameOrEvent.target
      setUserData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : eventValue
      }))
    } else {
      setUserData(prev => ({
        ...prev,
        [nameOrEvent]: value
      }))
    }
  }

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }))

    // Validate password when it changes
    if (field === "password") {
      const validation = validatePassword(value)
      setPasswordValidation(validation)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (isNewUser || (isProfilePage && passwordData.password)) {
      if (passwordData.password !== passwordData.confirmPassword) {
        toast.error("Οι κωδικοί πρόσβασης δεν ταιριάζουν")
        return
      }
    }

    if (isNewUser) {
      if (typeof userData.RoleID !== "number") {
        toast.error("Παρακαλώ επιλέξτε ρόλο χρήστη")
        return
      }

      if (!userData.LoginUsername) {
        toast.error("Παρακαλώ συμπληρώστε το username")
        return
      }

      if (!passwordData.password) {
        toast.error("Παρακαλώ συμπληρώστε τον κωδικό πρόσβασης")
        return
      }

      // Validate password for new users
      if (!passwordValidation.isValid) {
        toast.error("Παρακαλώ διορθώστε τον κωδικό πρόσβασης: " + passwordValidation.errors.join(", "))
        return
      }
    }

    // Validate password for profile changes
    if (isProfilePage && passwordData.password) {
      if (!passwordValidation.isValid) {
        toast.error("Παρακαλώ διορθώστε τον κωδικό πρόσβασης: " + passwordValidation.errors.join(", "))
        return
      }
    }

    // For profile page, verify current password if changing password
    if (isProfilePage && passwordData.password) {
      if (!passwordData.currentPassword) {
        toast.error("Παρακαλώ εισάγετε τον τρέχοντα κωδικό σας")
        return
      }

      setIsSubmitting(true)
      try {
        // Verify current password by attempting to login
        //TODO: Move this to the backend
        const verifyResponse = await login(user.username, passwordData.currentPassword, true)

        if (!verifyResponse) {
          toast.error("Ο τρέχων κωδικός είναι λάθος.")
          setIsSubmitting(false)
          return
        }
      } catch (error) {
        console.error("Password verification error:", error)
        toast.error("Αποτυχία επαλήθευσης τρέχοντος κωδικού")
        setIsSubmitting(false)
        return
      }
    }

    // Don't set isSubmitting here since it's already set if we're verifying password
    if (!isSubmitting) {
      setIsSubmitting(true)
    }
    try {
      const dataToSend = { ...userData }

      // Add password if it's provided (for new user or profile password change)
      if (passwordData.password) {
        dataToSend.LoginPassword = passwordData.password
      }

      if (isNewUser) {
        const response = await post("/users", dataToSend)
        if (response.success) {
          if (response.data?.IsSuccess === false) {
            toast.error(response.data.Problem || "Σφάλμα κατά τη δημιουργία χρήστη")
            return
          }
          toast.success("Ο χρήστης δημιουργήθηκε επιτυχώς", { autoClose: 3000 })
          navigate("/users")
        }
      } else {
        const response = await put(`/users/${userData.UserID}`, dataToSend)
        if (response.success && response.data?.IsSuccess === true) {
          if (isProfilePage || userData.UserID === user?.id) {
            toast.warning(
              "Οι αλλαγές στο προφίλ σας θα εφαρμοστούν πλήρως μετά την επόμενη σύνδεσή σας. Παρακαλώ αποσυνδεθείτε και συνδεθείτε ξανά.",
              {
                closeOnClick: false,
                draggable: false,
                closeButton: false
              }
            )
            navigate("/profile")
          } else {
            toast.success("Τα στοιχεία του χρήστη ενημερώθηκαν επιτυχώς", { autoClose: 3000 })
            navigate(`/users/${userData.UserID}`)
          }
        } else {
          toast.error(response.data?.Problem || "Αποτυχία ενημέρωσης χρήστη")
        }
      }
    } catch (error) {
      toast.error(isNewUser ? "Αποτυχία δημιουργίας χρήστη" : "Αποτυχία ενημέρωσης χρήστη")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isAdmin() && !isProfilePage) {
    navigate("/")
    return null
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Layout>
    )
  }

  if (!userData) {
    return <Layout />
  }

  const canEditRole = !isProfilePage && isAdmin()
  const isOwnProfile = userData?.UserID === user?.id
  const canEditPassword = isNewUser || isProfilePage || isAdmin()

  return (
    <Layout>
      {/* User Details Section */}
      <div className="content-glass mb-4 position-relative">
        {!isProfilePage && <BackButton className="mb-3" />}

        <div className="d-flex align-items-center mb-4">
          <i className="fas fa-user me-2" />
          <h5 className="mb-0">
            {isNewUser ? "Νέος Χρήστης" : isProfilePage ? "Προφίλ Χρήστη" : "Στοιχεία Χρήστη"}
          </h5>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            {!isNewUser && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label>User ID</Form.Label>
                  <Form.Control type="text" name="UserID" value={userData.UserID} disabled />
                </Form.Group>
              </Col>
            )}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Username</Form.Label>
                <Form.Control type="text" value={userData.LoginUsername} disabled />
              </Form.Group>
            </Col>
            {isNewUser && (
              <>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Ρόλος</Form.Label>
                    <Form.Select
                      name="RoleID"
                      value={userData.RoleID.toString()}
                      onChange={e =>
                        handleInputChange("RoleID", e.target.value === "" ? "" : parseInt(e.target.value, 10))
                      }
                      disabled={isSubmitting}
                      required
                      isInvalid={isNewUser && userData.RoleID === ""}>
                      <option value="">Επιλέξτε ρόλο</option>
                      {userRoles.map(role => (
                        <option key={role.Code} value={role.Code.toString()}>
                          {role.Name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="LoginUsername"
                      value={userData.LoginUsername}
                      onChange={e => handleInputChange("LoginUsername", e.target.value)}
                      disabled={isSubmitting}
                      required
                      isInvalid={isNewUser && !userData.LoginUsername}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            {!isNewUser && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Ρόλος</Form.Label>
                  {canEditRole && !isOwnProfile ? (
                    <Form.Select
                      name="RoleID"
                      value={userData.RoleID.toString()}
                      onChange={e =>
                        handleInputChange("RoleID", e.target.value === "" ? "" : parseInt(e.target.value, 10))
                      }
                      disabled={isSubmitting}
                      required
                      isInvalid={isNewUser && userData.RoleID === ""}>
                      <option value="">Επιλέξτε ρόλο</option>
                      {userRoles.map(role => (
                        <option key={role.Code} value={role.Code.toString()}>
                          {role.Name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={
                        userRoles.find(r => r.Code.toString() === userData.RoleID?.toString())?.Name || ""
                      }
                      disabled
                    />
                  )}
                </Form.Group>
              </Col>
            )}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Όνομα</Form.Label>
                <Form.Control
                  type="text"
                  name="Firstname"
                  value={userData.Firstname}
                  onChange={e => handleInputChange("Firstname", e.target.value)}
                  disabled={isSubmitting}
                  required={isNewUser}
                  isInvalid={isNewUser && !userData.Firstname}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Επώνυμο</Form.Label>
                <Form.Control
                  type="text"
                  name="LastName"
                  value={userData.LastName}
                  onChange={e => handleInputChange("LastName", e.target.value)}
                  disabled={isSubmitting}
                  required={isNewUser}
                  isInvalid={isNewUser && !userData.LastName}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="Mail"
                  value={userData.Mail}
                  onChange={e => handleInputChange("Mail", e.target.value)}
                  disabled={isSubmitting}
                  required={isNewUser}
                  isInvalid={isNewUser && !userData.Mail}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Τηλέφωνο</Form.Label>
                <Form.Control
                  type="text"
                  name="Phone"
                  value={userData.Phone}
                  onChange={e => handleInputChange("Phone", e.target.value)}
                  disabled={isSubmitting}
                  required={isNewUser}
                  isInvalid={isNewUser && !userData.Phone}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Κινητό</Form.Label>
                <Form.Control
                  type="text"
                  name="Mobile"
                  value={userData.Mobile}
                  onChange={e => handleInputChange("Mobile", e.target.value)}
                  disabled={isSubmitting}
                  isInvalid={isNewUser && !userData.Mobile}
                />
              </Form.Group>
            </Col>
            {/* Password fields only for new users and profile page */}
            {canEditPassword && (
              <>
                {isProfilePage && (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Τρέχων Κωδικός</Form.Label>
                      <Form.Control
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={e => handlePasswordChange("currentPassword", e.target.value)}
                        disabled={isSubmitting}
                        required={!!passwordData.password && !!passwordData.confirmPassword}
                        isInvalid={
                          !!passwordData.password &&
                          !!passwordData.confirmPassword &&
                          !passwordData.currentPassword
                        }
                      />
                    </Form.Group>
                  </Col>
                )}
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{isNewUser ? "Κωδικός" : "Νέος Κωδικός"}</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.password}
                      onChange={e => handlePasswordChange("password", e.target.value)}
                      disabled={isSubmitting}
                      required={isNewUser}
                      isInvalid={canEditPassword && passwordData.password && !passwordValidation.isValid}
                    />
                    {canEditPassword && passwordData.password && !passwordValidation.isValid && (
                      <Form.Control.Feedback type="invalid">
                        {passwordValidation.errors.map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </Form.Control.Feedback>
                    )}
                    {passwordData.confirmPassword &&
                      passwordData.password !== passwordData.confirmPassword && (
                        <Form.Control.Feedback type="invalid">
                          Οι κωδικοί πρόσβασης δεν ταιριάζουν
                        </Form.Control.Feedback>
                      )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Επιβεβαίωση Κωδικού</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={e => handlePasswordChange("confirmPassword", e.target.value)}
                      disabled={isSubmitting}
                      required={isNewUser || !!passwordData.password}
                      isInvalid={
                        (isNewUser || !!passwordData.password) &&
                        (!passwordData.confirmPassword ||
                          passwordData.password !== passwordData.confirmPassword)
                      }
                    />
                    {passwordData.confirmPassword &&
                      passwordData.password !== passwordData.confirmPassword && (
                        <Form.Control.Feedback type="invalid">
                          Οι κωδικοί πρόσβασης δεν ταιριάζουν
                        </Form.Control.Feedback>
                      )}
                  </Form.Group>
                </Col>
              </>
            )}
            {canEditRole && !isOwnProfile && (
              <Col md={4}>
                <Form.Group className="mt-4">
                  <Form.Check
                    type="checkbox"
                    name="Active"
                    label="Ενεργός"
                    checked={!!userData.Active}
                    onChange={e => handleInputChange("Active", e.target.checked ? 1 : 0)}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={
                isSubmitting ||
                (isNewUser &&
                  (!/\D/.test(!userData.RoleID) ||
                    !userData.LoginUsername ||
                    !userData.Firstname ||
                    !userData.LastName ||
                    !userData.Mail ||
                    !userData.Phone ||
                    !userData.Mobile ||
                    !passwordData.password ||
                    !passwordData.confirmPassword ||
                    passwordData.password !== passwordData.confirmPassword ||
                    !passwordValidation.isValid)) ||
                (!isNewUser &&
                  !!passwordData.password &&
                  !!passwordData.confirmPassword &&
                  ((!passwordData.currentPassword && isProfilePage) ||
                    passwordData.password !== passwordData.confirmPassword ||
                    !passwordValidation.isValid))
              }>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {isNewUser ? "Δημιουργία..." : "Αποθήκευση..."}
                </>
              ) : (
                <>
                  <i className={`fas ${isNewUser ? "fa-plus" : "fa-save"} me-2`} />
                  {isNewUser ? "Δημιουργία Χρήστη" : "Αποθήκευση"}
                </>
              )}
            </Button>
          </div>
        </Form>
      </div>

      {!isNewUser && userData && (
        <>
          {/* Working Hours Section */}
          <div className="content-glass mb-4">
            <div className="d-flex align-items-center mb-4">
              <i className="fas fa-clock me-2" />
              <h5 className="mb-0">Ωράριο Εργασίας</h5>
            </div>
            <WorkingDaysForm
              expertId={userData.UserID}
              initialData={isProfilePage ? user.WorkingDays : null}
              disabled={isSubmitting}
              onChange={() => {
                toast.success("Το ωράριο εργασίας αποθηκεύτηκε επιτυχώς", { autoClose: 3000 })
                if (isProfilePage || isOwnProfile) {
                  toast.warning(
                    "Οι αλλαγές στο ωράριο εργασίας θα εφαρμοστούν πλήρως μετά την επόμενη σύνδεσή σας. Παρακαλώ αποσυνδεθείτε και συνδεθείτε ξανά.",
                    {
                      closeOnClick: false,
                      draggable: false,
                      closeButton: false
                    }
                  )
                }
              }}
            />
          </div>

          {/* Out of Office Section */}
          <div className="content-glass">
            <div className="d-flex align-items-center mb-4">
              <i className="fas fa-calendar-times me-2" />
              <h5 className="mb-0">Περίοδοι Εκτός Λειτουργίας</h5>
            </div>
            <OutOfOfficeForm
              expertId={userData.UserID}
              initialData={isProfilePage ? user.OutOfOffice : null}
              disabled={isSubmitting}
            />
          </div>
        </>
      )}
    </Layout>
  )
}

export default UserDetails
